import React, { useState } from 'react';
import { formatDateOnly, formatLocalDate, parseLocalDate } from '../utils/dateUtils';
import EditClientForm from './EditClientForm';

const ClientCard = ({ client, onSelect, onNewMeeting, onUpdateClient, onDeleteClient, onAssignClient, canEdit = false, currentUser, permissions }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  
  const totalPatients = client.meetings.reduce((total, meeting) => total + meeting.patients, 0);
  const totalContacts = client.meetings.reduce((total, meeting) => total + meeting.contactsGenerated, 0);
  const conversionRate = totalContacts > 0 ? (totalPatients / totalContacts * 100).toFixed(1) + '%' : '0%';

  const generateClientReport = () => {
    let report = `--- REPORTE DE CLIENTE: ${client.name.toUpperCase()} ---\n\n`;
    report += `Nombre: ${client.name}\n`;
    if (client.specialty) report += `Especialidad: ${client.specialty}\n`;
    if (client.cell) report += `Célula: ${client.cell}\n`;
    if (client.email) report += `Email: ${client.email}\n`;
    if (client.country) report += `País: ${client.country}\n\n`;

    report += `--- RENDIMIENTO GENERAL ---\n`;
    report += `Total Juntas: ${client.meetings.length}\n`;
    report += `Contactos Generados: ${totalContacts}\n`;
    report += `Pacientes Concretados: ${totalPatients}\n`;
    report += `Tasa de Conversión: ${conversionRate}\n\n`;

    report += `--- HISTORIAL DE JUNTAS ---\n`;
    if (client.meetings.length === 0) {
      report += `No hay juntas registradas para este cliente.\n`;
    } else {
      client.meetings.slice().reverse().forEach((meeting, index) => {
        const contacts = meeting.contactsGenerated;
        const patients = meeting.patients;
        const conv = contacts > 0 ? ((patients / contacts) * 100).toFixed(1) + '%' : '0%';

        report += `\nJunta #${client.meetings.length - index} - Fecha: ${formatDateOnly(meeting.date)}\n`;
        report += `  Tipo de Junta: ${meeting.meetingType || 'N/A'}\n`;
        report += `  Resumen: ${meeting.observations}\n`;
        report += `  Acciones: ${meeting.agreements}\n`;
        report += `  Contactos: ${contacts}, Pacientes: ${patients}, Conversión: ${conv}\n`;
        if (meeting.nextMeetingDate) report += `  Próxima Junta: ${formatDateOnly(meeting.nextMeetingDate)} a las ${meeting.nextMeetingTime || 'N/A'} hrs\n`;
        if (meeting.clientMeetingEmail) report += `  Email Agendamiento: ${meeting.clientMeetingEmail}\n`;
        
        const totalObjections = (meeting.objections?.price || 0) + (meeting.objections?.location || 0) + (meeting.objections?.noResponse || 0) + (meeting.objections?.schedule || 0);
        if (totalObjections > 0 || meeting.objections?.other) {
          report += `  Objeciones:\n`;
          if (meeting.objections.price > 0) report += `    - Precio: ${meeting.objections.price} personas\n`;
          if (meeting.objections.location > 0) report += `    - Ubicación: ${meeting.objections.location} personas\n`;
          if (meeting.objections.noResponse > 0) report += `    - No contestan 1er mensaje: ${meeting.objections.noResponse} personas\n`;
          if (meeting.objections.schedule > 0) report += `    - Horarios: ${meeting.objections.schedule} personas\n`;
          if (meeting.objections.other) report += `    - Otras: ${meeting.objections.other}\n`;
        }
        const totalServicesValue = (meeting.servicesSold || []).reduce((sum, service) => sum + (service.quantity * service.value), 0);
        if (totalServicesValue > 0) {
          report += `  Servicios Vendidos:\n`;
          (meeting.servicesSold || []).forEach(service => {
            if (service.name) report += `    - ${service.name}: ${service.quantity} unidades, Valor: $${service.value.toFixed(2)} c/u (Total: ${(service.quantity * service.value).toFixed(2)})\n`;
          });
          report += `    Total Valor: $${totalServicesValue.toFixed(2)}\n`;
        }
      });
    }
    report += `\n--- FIN DEL REPORTE ---`;
    return report;
  };

  const generatePDFReport = () => {
    console.log('generatePDFReport - Iniciando generación de PDF para:', client.name);
    
    try {
      const reportContent = generateClientReport();
      console.log('generatePDFReport - Reporte generado, abriendo ventana...');
      
      // Crear una nueva ventana para imprimir
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        console.error('generatePDFReport - No se pudo abrir la ventana. Bloqueador de pop-ups?');
        alert('No se pudo abrir la ventana de impresión. Por favor, permite pop-ups para este sitio.');
        return;
      }
      
      console.log('generatePDFReport - Ventana abierta exitosamente');
    
      // Generar HTML formateado para impresión
      const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reporte de Cliente - ${client.name}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            line-height: 1.6;
            color: #333;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #0061A8;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .header h1 {
            color: #0061A8;
            margin: 0;
            font-size: 24px;
          }
          .header h2 {
            color: #666;
            margin: 5px 0 0 0;
            font-size: 18px;
            font-weight: normal;
          }
          .section {
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          .section-title {
            background-color: #0061A8;
            color: white;
            padding: 8px 15px;
            margin: 0 0 15px 0;
            font-size: 16px;
            font-weight: bold;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-bottom: 20px;
          }
          .info-item {
            padding: 8px;
            border-left: 3px solid #A8D08D;
            background-color: #f9f9f9;
          }
          .info-label {
            font-weight: bold;
            color: #0061A8;
          }
          .metrics {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin: 20px 0;
          }
          .metric-card {
            text-align: center;
            padding: 15px;
            border: 1px solid #ddd;
            border-radius: 5px;
            background-color: #f8f9fa;
          }
          .metric-value {
            font-size: 24px;
            font-weight: bold;
            color: #0061A8;
            margin: 5px 0;
          }
          .metric-label {
            font-size: 12px;
            color: #666;
            text-transform: uppercase;
          }
          .meeting {
            border: 1px solid #ddd;
            margin-bottom: 20px;
            border-radius: 5px;
            overflow: hidden;
            page-break-inside: avoid;
          }
          .meeting-header {
            background-color: #f8f9fa;
            padding: 10px 15px;
            border-bottom: 1px solid #ddd;
            font-weight: bold;
            color: #0061A8;
          }
          .meeting-content {
            padding: 15px;
          }
          .meeting-detail {
            margin-bottom: 10px;
          }
          .meeting-detail strong {
            color: #0061A8;
            display: inline-block;
            width: 120px;
          }
          .objections, .services {
            background-color: #f8f9fa;
            padding: 10px;
            margin: 10px 0;
            border-radius: 3px;
          }
          .no-data {
            color: #999;
            font-style: italic;
            text-align: center;
            padding: 20px;
          }
          @media print {
            body { margin: 0; }
            .section { page-break-inside: avoid; }
            .meeting { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>REPORTE DE CLIENTE</h1>
          <h2>${client.name.toUpperCase()}</h2>
          <p>Generado el ${formatDateOnly(new Date())}</p>
        </div>

        <div class="section">
          <div class="section-title">INFORMACIÓN DEL CLIENTE</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Nombre:</div>
              <div>${client.name}</div>
            </div>
            ${client.specialty ? `
            <div class="info-item">
              <div class="info-label">Especialidad:</div>
              <div>${client.specialty}</div>
            </div>` : ''}
            ${client.cell ? `
            <div class="info-item">
              <div class="info-label">Célula:</div>
              <div>${Array.isArray(client.cells) ? client.cells.join(', ') : client.cell}</div>
            </div>` : ''}
            ${client.email ? `
            <div class="info-item">
              <div class="info-label">Email:</div>
              <div>${client.email}</div>
            </div>` : ''}
            ${client.country ? `
            <div class="info-item">
              <div class="info-label">País:</div>
              <div>${client.country}</div>
            </div>` : ''}
            ${client.salesExecutives && client.salesExecutives.length > 0 ? `
            <div class="info-item">
              <div class="info-label">Ejecutivos:</div>
              <div>${client.salesExecutives.join(', ')}</div>
            </div>` : ''}
          </div>
        </div>

        <div class="section">
          <div class="section-title">RENDIMIENTO GENERAL</div>
          <div class="metrics">
            <div class="metric-card">
              <div class="metric-value">${client.meetings.length}</div>
              <div class="metric-label">Total Juntas</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${totalContacts}</div>
              <div class="metric-label">Contactos Generados</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${totalPatients}</div>
              <div class="metric-label">Pacientes Concretados</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${conversionRate}</div>
              <div class="metric-label">Tasa de Conversión</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">HISTORIAL DE JUNTAS</div>
          ${client.meetings.length === 0 ? `
            <div class="no-data">No hay juntas registradas para este cliente.</div>
          ` : client.meetings.slice().reverse().map((meeting, index) => {
            const contacts = meeting.contactsGenerated || 0;
            const patients = meeting.patients || 0;
            const conv = contacts > 0 ? ((patients / contacts) * 100).toFixed(1) + '%' : '0%';
            const totalObjections = (meeting.objections?.price || 0) + (meeting.objections?.location || 0) + (meeting.objections?.noResponse || 0) + (meeting.objections?.schedule || 0);
            const totalServicesValue = (meeting.servicesSold || []).reduce((sum, service) => sum + (service.quantity * service.value), 0);

            return `
              <div class="meeting">
                <div class="meeting-header">
                  Junta #${client.meetings.length - index} - ${formatDateOnly(meeting.date)}
                </div>
                <div class="meeting-content">
                  <div class="meeting-detail">
                    <strong>Tipo:</strong> ${meeting.meetingType || 'N/A'}
                  </div>
                  <div class="meeting-detail">
                    <strong>Resumen:</strong> ${meeting.observations || 'Sin observaciones'}
                  </div>
                  <div class="meeting-detail">
                    <strong>Acciones:</strong> ${meeting.agreements || 'Sin acuerdos'}
                  </div>
                  <div class="meeting-detail">
                    <strong>Métricas:</strong> ${contacts} contactos, ${patients} pacientes, ${conv} conversión
                  </div>
                  ${meeting.nextMeetingDate ? `
                  <div class="meeting-detail">
                    <strong>Próxima Junta:</strong> ${formatDateOnly(meeting.nextMeetingDate)} a las ${meeting.nextMeetingTime || 'N/A'} hrs
                  </div>` : ''}
                  ${meeting.clientMeetingEmail ? `
                  <div class="meeting-detail">
                    <strong>Email:</strong> ${meeting.clientMeetingEmail}
                  </div>` : ''}
                  
                  ${totalObjections > 0 || meeting.objections?.other ? `
                  <div class="objections">
                    <strong>Objeciones Registradas:</strong><br>
                    ${meeting.objections.price > 0 ? `• Precio: ${meeting.objections.price} personas<br>` : ''}
                    ${meeting.objections.location > 0 ? `• Ubicación: ${meeting.objections.location} personas<br>` : ''}
                    ${meeting.objections.noResponse > 0 ? `• No contestan 1er mensaje: ${meeting.objections.noResponse} personas<br>` : ''}
                    ${meeting.objections.schedule > 0 ? `• Horarios: ${meeting.objections.schedule} personas<br>` : ''}
                    ${meeting.objections.other ? `• Otras: ${meeting.objections.other}<br>` : ''}
                  </div>` : ''}
                  
                  ${totalServicesValue > 0 ? `
                  <div class="services">
                    <strong>Servicios Vendidos:</strong><br>
                    ${(meeting.servicesSold || []).map(service => 
                      service.name ? `• ${service.name}: ${service.quantity} unidades, $${service.value} c/u (Total: $${(service.quantity * service.value).toFixed(2)})<br>` : ''
                    ).join('')}
                    <strong>Total Valor: $${totalServicesValue.toFixed(2)}</strong>
                  </div>` : ''}
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </body>
      </html>
    `;
    
    // Escribir el contenido en la nueva ventana
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
      // Esperar a que se cargue y luego abrir el diálogo de impresión
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };
      
      console.log('generatePDFReport - PDF generado exitosamente');
    } catch (error) {
      console.error('generatePDFReport - Error al generar PDF:', error);
      alert('Error al generar el PDF. Por favor, inténtalo de nuevo.');
    }
  };

  const handleDeleteClient = () => {
    if (onDeleteClient) {
      onDeleteClient(client.id);
      setShowDeleteConfirm(false);
    }
  };

  const handleEditClient = () => {
    if (onUpdateClient) {
      setShowEditForm(true);
    }
  };

  const handleSaveClient = async (updatedClient) => {
    try {
      await onUpdateClient(updatedClient);
      setShowEditForm(false);
    } catch (error) {
      console.error('Error al actualizar cliente:', error);
      alert('Error al actualizar el cliente. Por favor, inténtalo de nuevo.');
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
  };

  return (
    <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-blue-200 transform hover:-translate-y-1">
      {/* Header con gradiente */}
      <div className="bg-gradient-to-r from-[#0061A8] to-[#004d8a] p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
        
        <div className="relative z-10 flex justify-between items-start">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-100 transition-colors">{client.name}</h3>
            
            {client.specialty && (
              <div className="flex items-center text-blue-100 mb-2">
                <div className="p-1 bg-white/20 rounded-full mr-2">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <span className="text-sm font-medium">{client.specialty}</span>
              </div>
            )}
            
            {client.cell && (
              <div className="flex items-center text-blue-100 mb-2">
                <div className="p-1 bg-white/20 rounded-full mr-2">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <span className="text-sm font-medium">{Array.isArray(client.cells) ? client.cells.join(', ') : client.cell}</span>
              </div>
            )}
            
            {(client.salesExecutive || (client.salesExecutives && client.salesExecutives.length > 0)) && (
              <div className="flex items-center text-green-200">
                <div className="p-1 bg-white/20 rounded-full mr-2">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="text-sm font-medium truncate">
                  {Array.isArray(client.salesExecutives) ? client.salesExecutives.join(', ') : client.salesExecutive}
                </span>
              </div>
            )}
            
            {client.email && (
              <div className="flex items-center text-blue-100 mt-2">
                <div className="p-1 bg-white/20 rounded-full mr-2">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-xs font-medium truncate" title={client.email}>
                  {client.email}
                </span>
              </div>
            )}
          </div>
          
          <div className="text-center">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
              <div className="text-2xl font-bold text-white">{client.meetings.length}</div>
              <div className="text-xs text-blue-100 font-medium">JUNTAS</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {/* Métricas Premium */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 group-hover:shadow-md transition-shadow">
            <div className="text-xs text-[#63666A] font-semibold mb-1 uppercase tracking-wide truncate">Contactos</div>
            <div className="text-xl font-bold text-[#0061A8] truncate">{totalContacts}</div>
          </div>
          <div className="text-center p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200 group-hover:shadow-md transition-shadow">
            <div className="text-xs text-[#63666A] font-semibold mb-1 uppercase tracking-wide truncate">Pacientes</div>
            <div className="text-xl font-bold text-[#A8D08D] truncate">{totalPatients}</div>
          </div>
          <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200 group-hover:shadow-md transition-shadow">
            <div className="text-xs text-[#63666A] font-semibold mb-1 uppercase tracking-wide truncate">Conversión</div>
            <div className={`text-xl font-bold truncate ${
              parseFloat(conversionRate) > 70 ? 'text-green-600' :
              parseFloat(conversionRate) > 30 ? 'text-yellow-600' :
              parseFloat(conversionRate) > 0 ? 'text-orange-600' : 'text-gray-400'
            }`}>
              {conversionRate}
            </div>
          </div>
        </div>

        {/* Botones de acción Premium */}
        <div className="space-y-3">
          {/* Fila 1: Acciones principales */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onSelect(client)}
              className="group/btn flex items-center justify-center space-x-2 py-3 px-4 bg-gradient-to-r from-[#0061A8] to-[#004d8a] text-white rounded-xl hover:from-[#004d8a] hover:to-[#0061A8] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span className="text-sm font-semibold">Ver Historial</span>
            </button>
            
            {onNewMeeting && (
              <button
                onClick={() => onNewMeeting(client)}
                className="group/btn flex items-center justify-center space-x-2 py-3 px-4 bg-gradient-to-r from-[#A8D08D] to-[#8bc34a] text-white rounded-xl hover:from-[#8bc34a] hover:to-[#A8D08D] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="text-sm font-semibold">Nueva Junta</span>
              </button>
            )}
          </div>

          {/* Fila 2: Acciones secundarias */}
          <div className={`grid gap-3 ${
            (currentUser?.role === 'admin' && canEdit && onUpdateClient) ? 'grid-cols-2' : 
            (currentUser?.role === 'admin' || (canEdit && onUpdateClient) || onAssignClient) ? 'grid-cols-1' : 
            'grid-cols-1'
          }`}>
            {/* Botón PDF - Solo para administradores */}
            {currentUser?.role === 'admin' && (
              <button
                onClick={generatePDFReport}
                className="group/btn flex items-center justify-center space-x-2 py-3 px-4 bg-gradient-to-r from-[#63666A] to-[#4a4d52] text-white rounded-xl hover:from-[#4a4d52] hover:to-[#63666A] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm font-semibold">PDF Completo</span>
              </button>
            )}
            
            {canEdit && onUpdateClient && (
              <button
                onClick={handleEditClient}
                className="group/btn flex items-center justify-center space-x-2 py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="text-sm font-semibold">Editar</span>
              </button>
            )}
          </div>

          {/* Fila 3: Asignar Cliente */}
          {onAssignClient && (
            <div className="w-full">
              <button
                onClick={() => onAssignClient(client)}
                className="group/btn w-full flex items-center justify-center space-x-2 py-3 px-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="text-sm font-semibold">Asignar a Asesores</span>
              </button>
            </div>
          )}

          {/* Fila 3: Eliminar Cliente (solo si tiene permisos) */}
          {canEdit && onDeleteClient && (
            <div className="w-full">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="group/btn w-full flex items-center justify-center space-x-2 py-3 px-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-500 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="text-sm font-semibold">Eliminar Cliente</span>
                </button>
              ) : (
                <div className="flex space-x-3">
                  <button
                    onClick={handleDeleteClient}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-600 transition-all duration-300 text-sm font-semibold shadow-lg"
                  >
                    ✓ Confirmar
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl hover:from-gray-600 hover:to-gray-500 transition-all duration-300 text-sm font-semibold shadow-lg"
                  >
                    ✕ Cancelar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de edición */}
      {showEditForm && (
        <EditClientForm
          client={client}
          onSave={handleSaveClient}
          onCancel={handleCancelEdit}
        />
      )}
    </div>
  );
};

export default ClientCard;
